import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Shield, Users, FileText, DollarSign, HardHat, 
  TrendingUp, BarChart3, CheckCircle, XCircle,
  Eye, FileImage, Download, X, Star, Home, 
  Building2, Briefcase, Clock, Mail, Filter,
  ChevronRight, ChevronLeft, RefreshCw, Search, Network, MapPin, Sparkles, Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [portalStats, setPortalStats] = useState(null);
  const [earlyAccessStats, setEarlyAccessStats] = useState(null);
  const [demoSeeding, setDemoSeeding] = useState(false);
  const [demoActive, setDemoActive] = useState(false);
  const [pendingContractors, setPendingContractors] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Early Access state
  const [publicWaitlist, setPublicWaitlist] = useState([]);
  const [founderApplications, setFounderApplications] = useState([]);
  const [waitlistPage, setWaitlistPage] = useState(1);
  const [founderPage, setFounderPage] = useState(1);
  const [waitlistTotalPages, setWaitlistTotalPages] = useState(1);
  const [founderTotalPages, setFounderTotalPages] = useState(1);
  const [founderStatusFilter, setFounderStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Document viewer state
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [contractorDocs, setContractorDocs] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  
  // Application detail modal
  const [selectedApplication, setSelectedApplication] = useState(null);

  const { mainClass } = useSidebar();
  const isAdmin = user?.email && ["jamesdehart@dealforge.pro", "jdehart06@gmail.com", "TheDeHartInvestmentGroup@outlook.com", "admin@dealforge.com"].includes(user.email);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/dashboard");
      return;
    }
    fetchAdminData();
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === "early-access") {
      fetchEarlyAccessData();
    }
  }, [activeTab, waitlistPage, founderPage, founderStatusFilter]);

  const fetchAdminData = async () => {
    try {
      const [statsRes, contractorsRes, paymentsRes, portalStatsRes, demoStatusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/contractors/all`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/payments`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/portals/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/demo/status`, { withCredentials: true }).catch(() => ({ data: { demo_active: false } }))
      ]);
      
      const rawStats = statsRes.data;
      setStats({
        total_users: rawStats.users?.total || 0,
        new_users_today: rawStats.users?.new_today || 0,
        new_users_week: rawStats.users?.new_this_week || 0,
        total_deals: rawStats.deals?.total || 0,
        active_deals: rawStats.deals?.active || 0,
        closed_deals: rawStats.deals?.closed || 0,
        total_volume: rawStats.deals?.total_volume || 0,
        closed_volume: rawStats.deals?.closed_volume || 0,
        total_revenue: rawStats.deals?.total_volume || 0,
        mrr: 0,
        portals: rawStats.portals || {}
      });
      setPendingContractors(contractorsRes.data.filter(c => c.verification_status === "pending"));
      setRecentPayments(paymentsRes.data.slice(0, 10));
      setPortalStats(portalStatsRes.data);
      setEarlyAccessStats(portalStatsRes.data.early_access);
      setDemoActive(demoStatusRes.data.demo_active || false);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fetchEarlyAccessData = async () => {
    try {
      const [waitlistRes, founderRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/waitlist/admin/early-access/public-waitlist?page=${waitlistPage}&limit=10`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/waitlist/admin/early-access/founder-applications?page=${founderPage}&limit=10&status=${founderStatusFilter}`, { withCredentials: true })
      ]);
      
      setPublicWaitlist(waitlistRes.data.signups || []);
      setWaitlistTotalPages(waitlistRes.data.total_pages || 1);
      setFounderApplications(founderRes.data.applications || []);
      setFounderTotalPages(founderRes.data.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch early access data:", error);
    }
  };

  const handleUpdateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/waitlist/admin/early-access/founder-applications/${applicationId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Application ${newStatus}`);
      fetchEarlyAccessData();
      fetchAdminData();
      setSelectedApplication(null);
    } catch (error) {
      toast.error("Failed to update application");
    }
  };

  const handleExportWaitlist = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/waitlist/admin/early-access/export/public-waitlist`,
        { withCredentials: true, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `dealforge_public_waitlist_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleExportFounderApps = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/waitlist/admin/early-access/export/founder-applications`,
        { withCredentials: true, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `dealforge_founder_applications_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const viewContractorDocs = async (contractor) => {
    setSelectedContractor(contractor);
    setDocLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/contractors/${contractor.contractor_id}/documents`,
        { withCredentials: true }
      );
      setContractorDocs(response.data);
    } catch (error) {
      toast.error("Failed to load documents");
      setContractorDocs([]);
    } finally {
      setDocLoading(false);
    }
  };

  const handleVerifyContractor = async (contractorId, action) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/contractor/${contractorId}/verify`,
        { verified: action === 'approve' },
        { withCredentials: true }
      );
      toast.success(`Contractor ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchAdminData();
    } catch (error) {
      toast.error("Failed to update contractor");
    }
  };

  const formatCurrency = (val) => {
    const amount = val || 0;
    // Amounts stored in cents from Stripe
    const dollars = amount > 999 ? amount / 100 : amount;
    return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={`${mainClass} flex-1 p-4 md:p-8 flex items-center justify-center transition-all duration-300`}>
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin shadow-[0_0_15px_rgba(212,175,55,0.3)]" />
        </main>
      </div>
    );
  }

  const tabs = [
    { key: "overview", label: "Overview", icon: BarChart3 },
    { key: "early-access", label: "Early Access", icon: Mail },
    { key: "portals", label: "Portals", icon: Building2 },
    { key: "contractors", label: "Contractors", icon: HardHat }
  ];

  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`${mainClass} flex-1 p-4 md:p-8 transition-all duration-300`}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-white/50 text-sm">Deal Operations & Platform Overview</p>
          </div>
        </div>
        
        {/* Demo Mode toggle */}
        <div className="flex items-center gap-2 mb-6">
          <Button
            size="sm"
            onClick={async () => {
              setDemoSeeding(true);
              try {
                if (demoActive) {
                  await axios.delete(`${BACKEND_URL}/api/admin/demo/clear`, { withCredentials: true });
                  setDemoActive(false);
                  toast.success("Demo Mode OFF — demo data cleared");
                } else {
                  const res = await axios.post(`${BACKEND_URL}/api/admin/demo/seed`, {}, { withCredentials: true });
                  setDemoActive(true);
                  toast.success(`Demo Mode ON — ${res.data.seeded.deals} deals loaded across all portals`);
                }
              } catch (e) { toast.error(e.response?.data?.detail || "Failed to toggle demo mode"); }
              finally { setDemoSeeding(false); }
            }}
            disabled={demoSeeding}
            className={`font-bold text-xs gap-1.5 transition-all ${demoActive 
              ? "bg-emerald-500/20 hover:bg-red-500/20 text-emerald-400 hover:text-red-400 border border-emerald-500/30 hover:border-red-500/30" 
              : "bg-[#D4AF37] hover:bg-[#C9A227] text-black"}`}
            data-testid="demo-mode-toggle-btn"
          >
            {demoSeeding 
              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              : demoActive 
                ? <><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse inline-block mr-1" />Demo Mode: ON — Click to Clear</>
                : <><Sparkles className="w-3.5 h-3.5" /> Load Demo Data</>
            }
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/[0.08] overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab.key
                    ? "text-[#D4AF37] border-b-2 border-[#D4AF37]"
                    : "text-white/50 hover:text-white"
                }`}
                data-testid={`admin-tab-${tab.key}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === "overview" && (
          <>
            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-gold" />
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                </div>
                <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
                <p className="text-white/70 text-sm">Total Users</p>
              </div>

              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center justify-between mb-4">
                  <FileText className="w-8 h-8 text-gold" />
                </div>
                <p className="text-3xl font-bold text-white">{stats?.total_deals || 0}</p>
                <p className="text-white/70 text-sm">Total Deals</p>
              </div>

              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center justify-between mb-4">
                  <DollarSign className="w-8 h-8 text-emerald-500" />
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                </div>
                <p className="text-3xl font-bold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">{formatCurrency(stats?.total_volume)}</p>
                <p className="text-white/70 text-sm">Pipeline Value (ARV)</p>
              </div>

              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center justify-between mb-4">
                  <Mail className="w-8 h-8 text-blue-500" />
                </div>
                <p className="text-3xl font-bold text-white">
                  {(earlyAccessStats?.public_waitlist?.total || 0) + (earlyAccessStats?.founder_applications?.total || 0)}
                </p>
                <p className="text-white/70 text-sm">Early Access Signups</p>
              </div>
            </div>

            {/* Quick Stats Row - Partner Network */}
            <div className="grid lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
                <p className="text-white/50 text-sm">Realtors</p>
                <p className="text-2xl font-bold text-white">{stats?.portals?.realtors || portalStats?.realtor_portal?.total_realtors || 0}</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
                <p className="text-white/50 text-sm">Lenders</p>
                <p className="text-2xl font-bold text-white">{stats?.portals?.lenders || portalStats?.lender_portal?.total_lenders || 0}</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
                <p className="text-white/50 text-sm">Contractors</p>
                <p className="text-2xl font-bold text-white">{stats?.portals?.contractors || portalStats?.contractor_portal?.total_contractors || 0}</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
                <p className="text-white/50 text-sm">Title Companies</p>
                <p className="text-2xl font-bold text-white">{stats?.portals?.title_companies || portalStats?.title_portal?.total_companies || 0}</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Payments */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
                  <h2 className="text-white font-bold flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-gold" />
                    Recent Payments
                  </h2>
                  <Link to="/admin/payments" className="text-gold text-sm hover:underline">View All</Link>
                </div>
                <div className="divide-y divide-white/[0.06] max-h-80 overflow-y-auto">
                  {recentPayments.slice(0, 5).map(payment => (
                    <div key={payment.transaction_id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{payment.email}</p>
                        <p className="text-white/70 text-xs">{payment.tier} plan</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-mono font-bold ${payment.payment_status === 'paid' ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-white/70'}`}>
                          {formatCurrency(payment.amount)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending Contractors */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
                  <h2 className="text-white font-bold flex items-center gap-2">
                    <HardHat className="w-5 h-5 text-gold" />
                    Pending Verifications
                  </h2>
                  <span className="text-white/70 text-sm">{pendingContractors.length} pending</span>
                </div>
                {pendingContractors.length === 0 ? (
                  <div className="p-8 text-center text-white/70">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No pending verifications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/[0.06] max-h-80 overflow-y-auto">
                    {pendingContractors.slice(0, 5).map(contractor => (
                      <div key={contractor.contractor_id} className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-medium">{contractor.company_name}</p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleVerifyContractor(contractor.contractor_id, "approve")}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none h-8"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleVerifyContractor(contractor.contractor_id, "reject")}
                              className="border-red-500 text-red-500 hover:bg-red-500/10 rounded-none h-8"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-white/70 text-sm">{contractor.email}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/admin/users" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/50 transition-all duration-300" data-testid="admin-link-users">
                <Users className="w-6 h-6 text-gold mb-2" />
                <p className="text-white font-bold">Users</p>
              </Link>
              <Link to="/admin/deals" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/50 transition-all duration-300" data-testid="admin-link-deals">
                <FileText className="w-6 h-6 text-gold mb-2" />
                <p className="text-white font-bold">Deals</p>
              </Link>
              <Link to="/admin/payments" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/50 transition-all duration-300" data-testid="admin-link-payments">
                <DollarSign className="w-6 h-6 text-gold mb-2" />
                <p className="text-white font-bold">Payments</p>
              </Link>
              <Link to="/admin/waitlist" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/50 transition-all duration-300" data-testid="admin-link-waitlist">
                <Mail className="w-6 h-6 text-gold mb-2" />
                <p className="text-white font-bold">Legacy Waitlist</p>
              </Link>
              <Link to="/admin/referrals" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#D4AF37]/20 p-4 hover:border-[#D4AF37]/50 transition-all duration-300" data-testid="admin-link-referrals">
                <Network className="w-6 h-6 text-[#D4AF37] mb-2" />
                <p className="text-white font-bold">Network Credits</p>
                <p className="text-white/30 text-xs">Approve referrals · Release credits</p>
              </Link>
            </div>
          </>
        )}

        {/* EARLY ACCESS TAB */}
        {activeTab === "early-access" && (
          <>
            {/* Stats Cards */}
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                  <span className="text-white/70 text-sm">Public Waitlist</span>
                </div>
                <p className="text-3xl font-bold text-white">{earlyAccessStats?.public_waitlist?.total || 0}</p>
                <p className="text-white/40 text-xs">+{earlyAccessStats?.public_waitlist?.this_week || 0} this week</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Briefcase className="w-5 h-5 text-[#FF3B30]" />
                  <span className="text-white/70 text-sm">Founder Apps</span>
                </div>
                <p className="text-3xl font-bold text-white">{earlyAccessStats?.founder_applications?.total || 0}</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-amber-500/50 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" />
                  <span className="text-white/70 text-sm">Pending Review</span>
                </div>
                <p className="text-3xl font-bold text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]">{earlyAccessStats?.founder_applications?.pending || 0}</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-emerald-500/50 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                  <span className="text-white/70 text-sm">Approved</span>
                </div>
                <p className="text-3xl font-bold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">{earlyAccessStats?.founder_applications?.approved || 0}</p>
              </div>
            </div>

            {/* Top Markets Panel */}
            {earlyAccessStats?.founder_applications?.by_market && Object.keys(earlyAccessStats.founder_applications.by_market).length > 0 && (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                  <h3 className="text-white font-bold">Top Markets</h3>
                  <span className="text-white/40 text-sm ml-auto">{Object.keys(earlyAccessStats.founder_applications.by_market).length} markets</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {Object.entries(earlyAccessStats.founder_applications.by_market)
                    .sort((a, b) => b[1] - a[1])
                    .map(([market, count]) => (
                      <div 
                        key={market} 
                        className="bg-black/50 border border-white/[0.06] p-3 hover:border-[#0A84FF]/30 transition-colors"
                      >
                        <p className="text-white font-medium text-sm truncate" title={market}>{market}</p>
                        <p className="text-[#0A84FF] drop-shadow-[0_0_6px_rgba(10,132,255,0.4)] text-2xl font-bold">{count}</p>
                        <p className="text-white/40 text-xs">applicants</p>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Public Waitlist */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
                  <h2 className="text-white font-bold">Public Launch Waitlist</h2>
                  <Button onClick={handleExportWaitlist} variant="outline" size="sm" className="border-white/[0.1] text-white/70 rounded-none">
                    <Download className="w-4 h-4 mr-2" />Export
                  </Button>
                </div>
                <div className="divide-y divide-white/[0.06] max-h-96 overflow-y-auto">
                  {publicWaitlist.map(signup => (
                    <div key={signup.waitlist_id} className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-white font-medium">{signup.first_name}</p>
                        <span className="px-2 py-0.5 bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] text-xs">{signup.role}</span>
                      </div>
                      <p className="text-white/50 text-sm">{signup.email}</p>
                      <p className="text-white/40 text-xs mt-1">{signup.market} • {formatDate(signup.created_at)}</p>
                    </div>
                  ))}
                </div>
                {waitlistTotalPages > 1 && (
                  <div className="p-3 border-t border-white/[0.08] flex items-center justify-between">
                    <span className="text-white/50 text-sm">Page {waitlistPage} of {waitlistTotalPages}</span>
                    <div className="flex gap-2">
                      <Button onClick={() => setWaitlistPage(p => Math.max(1, p - 1))} disabled={waitlistPage === 1} variant="ghost" size="sm" className="text-white/70">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => setWaitlistPage(p => Math.min(waitlistTotalPages, p + 1))} disabled={waitlistPage === waitlistTotalPages} variant="ghost" size="sm" className="text-white/70">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Founder Applications */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
                  <h2 className="text-white font-bold">Founder Beta Applications</h2>
                  <div className="flex items-center gap-2">
                    <Select value={founderStatusFilter} onValueChange={setFounderStatusFilter}>
                      <SelectTrigger className="bg-black border-white/[0.08] text-white h-8 w-28 rounded-none text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-white/[0.08]">
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleExportFounderApps} variant="outline" size="sm" className="border-white/[0.1] text-white/70 rounded-none">
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="divide-y divide-white/[0.06] max-h-96 overflow-y-auto">
                  {founderApplications.map(app => (
                    <div 
                      key={app.application_id} 
                      className="p-4 hover:bg-white/[0.06]/30 cursor-pointer"
                      onClick={() => setSelectedApplication(app)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-white font-medium">{app.full_name}</p>
                        <span className={`px-2 py-0.5 text-xs ${
                          app.status === "approved" ? "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
                          app.status === "declined" ? "bg-red-500/20 text-[#FF3B30]" :
                          "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]"
                        }`}>
                          {app.status}
                        </span>
                      </div>
                      <p className="text-white/50 text-sm">{app.email}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-white/40 text-xs">{app.role}</span>
                        <span className="text-white/30">•</span>
                        {app.primary_market && (
                          <>
                            <span className="text-[#0A84FF] drop-shadow-[0_0_6px_rgba(10,132,255,0.4)] text-xs font-medium">{app.primary_market}</span>
                            <span className="text-white/30">•</span>
                          </>
                        )}
                        <span className="text-white/40 text-xs">{app.deals_closed_last_12_months || "N/A"} closed/yr</span>
                        <span className="text-white/30">•</span>
                        <span className="text-white/40 text-xs">{app.monthly_volume} deals/mo</span>
                        {app.volume_tier === "high_volume" && (
                          <>
                            <span className="text-white/30">•</span>
                            <span className="text-gold text-xs">High Volume</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {founderTotalPages > 1 && (
                  <div className="p-3 border-t border-white/[0.08] flex items-center justify-between">
                    <span className="text-white/50 text-sm">Page {founderPage} of {founderTotalPages}</span>
                    <div className="flex gap-2">
                      <Button onClick={() => setFounderPage(p => Math.max(1, p - 1))} disabled={founderPage === 1} variant="ghost" size="sm" className="text-white/70">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => setFounderPage(p => Math.min(founderTotalPages, p + 1))} disabled={founderPage === founderTotalPages} variant="ghost" size="sm" className="text-white/70">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* PORTALS TAB */}
        {activeTab === "portals" && (
          <>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Realtor Portal Stats */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08] flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center">
                    <Home className="w-5 h-5 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold">Realtor Portal</h2>
                    <p className="text-white/50 text-sm">/realtor</p>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-3 gap-4 border-b border-white/[0.08]">
                  <div>
                    <p className="text-white/50 text-xs">Total Realtors</p>
                    <p className="text-2xl font-bold text-white">{portalStats?.realtor_portal?.total_realtors || 0}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Active Listings</p>
                    <p className="text-2xl font-bold text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]">{portalStats?.realtor_portal?.active_listings || 0}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Transactions</p>
                    <p className="text-2xl font-bold text-white">{portalStats?.realtor_portal?.total_transactions || 0}</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-white/50 text-sm mb-3">Recent Signups</p>
                  <div className="space-y-2">
                    {(portalStats?.realtor_portal?.recent_realtors || []).map(r => (
                      <div key={r.realtor_id} className="flex items-center justify-between py-2 border-b border-white/[0.08]">
                        <div>
                          <p className="text-white text-sm">{r.full_name || r.email}</p>
                          <p className="text-white/40 text-xs">{r.email}</p>
                        </div>
                        <span className="text-white/50 text-xs">{formatDate(r.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Title Portal Stats */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08] flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold">Title Portal</h2>
                    <p className="text-white/50 text-sm">/title</p>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-3 gap-4 border-b border-white/[0.08]">
                  <div>
                    <p className="text-white/50 text-xs">Title Companies</p>
                    <p className="text-2xl font-bold text-white">{portalStats?.title_portal?.total_companies || 0}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Active Orders</p>
                    <p className="text-2xl font-bold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">{portalStats?.title_portal?.active_orders || 0}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Total Orders</p>
                    <p className="text-2xl font-bold text-white">{portalStats?.title_portal?.total_orders || 0}</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-white/50 text-sm mb-3">Orders by Stage</p>
                  <div className="space-y-2">
                    {Object.entries(portalStats?.title_portal?.orders_by_stage || {}).map(([stage, count]) => (
                      <div key={stage} className="flex items-center justify-between">
                        <span className="text-white/70 text-sm capitalize">{stage.replace(/_/g, " ")}</span>
                        <span className="text-white font-mono">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Lender Portal Stats */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08] flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold">Lender Portal</h2>
                    <p className="text-white/50 text-sm">/lender</p>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-3 gap-4 border-b border-white/[0.08]">
                  <div>
                    <p className="text-white/50 text-xs">Total Lenders</p>
                    <p className="text-2xl font-bold text-white">{portalStats?.lender_portal?.total_lenders || 0}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Active Loans</p>
                    <p className="text-2xl font-bold text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]">{portalStats?.lender_portal?.active_loans || 0}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Total Funded</p>
                    <p className="text-2xl font-bold text-white">${((portalStats?.lender_portal?.total_funded || 0) / 1000000).toFixed(1)}M</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-white/50 text-sm mb-3">Loan Types</p>
                  <div className="space-y-2">
                    {Object.entries(portalStats?.lender_portal?.loans_by_type || {}).length > 0 ? (
                      Object.entries(portalStats?.lender_portal?.loans_by_type || {}).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between">
                          <span className="text-white/70 text-sm capitalize">{type.replace(/_/g, " ")}</span>
                          <span className="text-white font-mono">{count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-white/40 text-sm">No loans yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contractor Portal Stats */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08] flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center">
                    <HardHat className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold">Contractor Portal</h2>
                    <p className="text-white/50 text-sm">/contractor</p>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-3 gap-4 border-b border-white/[0.08]">
                  <div>
                    <p className="text-white/50 text-xs">Total Contractors</p>
                    <p className="text-2xl font-bold text-white">{portalStats?.contractor_portal?.total_contractors || 0}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Active Bids</p>
                    <p className="text-2xl font-bold text-[#D4AF37]">{portalStats?.contractor_portal?.active_bids || 0}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Verified</p>
                    <p className="text-2xl font-bold text-white">{portalStats?.contractor_portal?.verified_count || 0}</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-white/50 text-sm mb-3">By Specialty</p>
                  <div className="space-y-2">
                    {Object.entries(portalStats?.contractor_portal?.by_specialty || {}).length > 0 ? (
                      Object.entries(portalStats?.contractor_portal?.by_specialty || {}).slice(0, 5).map(([specialty, count]) => (
                        <div key={specialty} className="flex items-center justify-between">
                          <span className="text-white/70 text-sm capitalize">{specialty}</span>
                          <span className="text-white font-mono">{count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-white/40 text-sm">No specialties yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* CONTRACTORS TAB */}
        {activeTab === "contractors" && (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
            <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
              <h2 className="text-white font-bold flex items-center gap-2">
                <HardHat className="w-5 h-5 text-gold" />
                Contractor Verifications
              </h2>
              <span className="text-white/70 text-sm">{pendingContractors.length} pending</span>
            </div>

            {pendingContractors.length === 0 ? (
              <div className="p-12 text-center text-white/70">
                <CheckCircle className="w-16 h-16 mx-auto mb-3 opacity-50" />
                <p className="text-lg">All contractors verified</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.06]">
                {pendingContractors.map(contractor => (
                  <div key={contractor.contractor_id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-white font-medium">{contractor.company_name}</p>
                        <p className="text-white/70 text-sm">{contractor.email}</p>
                      </div>
                      {contractor.average_rating && (
                        <div className="flex items-center gap-1 text-gold">
                          <Star className="w-4 h-4 fill-current" />
                          <span>{contractor.average_rating}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-white/70 text-xs mb-3">
                      {contractor.specialties?.join(", ") || "No specialties listed"}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewContractorDocs(contractor)}
                        className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
                      >
                        <Eye className="w-4 h-4 mr-1" />Docs ({contractor.documents?.length || 0})
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleVerifyContractor(contractor.contractor_id, "approve")}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleVerifyContractor(contractor.contractor_id, "reject")}
                        className="border-red-500 text-red-500 hover:bg-red-500/10 rounded-none"
                      >
                        <XCircle className="w-4 h-4 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Founder Application Detail Modal */}
      <Dialog open={!!selectedApplication} onOpenChange={() => setSelectedApplication(null)}>
        <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#FF3B30]" />
              Founder Application: {selectedApplication?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedApplication && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs">Email</p>
                  <p className="text-white">{selectedApplication.email}</p>
                </div>
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs">Role</p>
                  <p className="text-white capitalize">{selectedApplication.role}</p>
                </div>
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs">Primary Market</p>
                  <p className="text-white">{selectedApplication.primary_market}</p>
                </div>
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs">Deals Closed (Last 12 Months)</p>
                  <p className="text-white font-medium text-gold">{selectedApplication.deals_closed_last_12_months || "Not specified"}</p>
                </div>
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs">Monthly Volume</p>
                  <p className="text-white">{selectedApplication.monthly_volume} deals</p>
                </div>
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs">Annual Volume</p>
                  <p className="text-white">{selectedApplication.annual_volume || "Not specified"}</p>
                </div>
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs">Volume Tier</p>
                  <p className={`font-medium ${selectedApplication.volume_tier === "high_volume" ? "text-gold" : "text-white"}`}>
                    {selectedApplication.volume_tier?.replace(/_/g, " ")}
                  </p>
                </div>
              </div>
              
              {selectedApplication.bottleneck && (
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs mb-1">Biggest Operational Bottleneck</p>
                  <p className="text-white/70 text-sm">{selectedApplication.bottleneck}</p>
                </div>
              )}
              
              {selectedApplication.why_access && (
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs mb-1">Why Early Access?</p>
                  <p className="text-white/70 text-sm">{selectedApplication.why_access}</p>
                </div>
              )}
              
              {selectedApplication.website && (
                <div className="bg-black p-3">
                  <p className="text-white/50 text-xs">Website/LinkedIn</p>
                  <a href={selectedApplication.website} target="_blank" rel="noopener noreferrer" className="text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] hover:underline">
                    {selectedApplication.website}
                  </a>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t border-white/[0.08]">
                <div>
                  <span className={`px-3 py-1 text-sm ${
                    selectedApplication.status === "approved" ? "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
                    selectedApplication.status === "declined" ? "bg-red-500/20 text-[#FF3B30]" :
                    "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]"
                  }`}>
                    {selectedApplication.status}
                  </span>
                </div>
                {selectedApplication.status === "pending" && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.application_id, "approved")}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />Approve
                    </Button>
                    <Button
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.application_id, "declined")}
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-500/10 rounded-none"
                    >
                      <XCircle className="w-4 h-4 mr-2" />Decline
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
